// Marketing-site translation dictionary. Stage 1 covers the highest-
// traffic shared chrome: header CTA buttons, footer column headings +
// subscribe form, language-switcher captions, the hero on /, and the
// "Coming soon" affordance on coming-soon pages.
//
// Per-page body translations are NOT in here yet — they ship via
// Sanity (the editor can publish a per-locale section tree once
// translations exist for that page).
//
// To translate more strings: add the English key here, add the same
// key to es/fr/de, and the useTranslation() hook will pick it up.

export type LocaleCode = 'en' | 'es' | 'fr' | 'de'

export const LOCALE_COOKIE = 'idukki:locale'

export type Dictionary = {
  // Header / CTA
  header: {
    signIn: string
    startFree: string
    bookDemo: string
    talkToSales: string
    seeOpenRoles: string
    readTheRelease: string
  }
  // Announcement bar
  announce: {
    new: string
    cta: string
  }
  // Hero / homepage
  hero: {
    eyebrow: string
    headline: string
    sub: string
    primary: string
    secondary: string
  }
  // Footer
  footer: {
    newsletter: string
    nextPost: string
    monthly: string
    placeholder: string
    subscribe: string
    noSpam: string
    product: string
    solutions: string
    tools: string
    resources: string
    company: string
    rights: string
  }
  // Language switcher caption
  switcher: {
    label: string
    soon: string
    soonTooltip: string
    caption: string
    playLink: string
  }
  // Coming-soon page chrome (shared across the 4 speculative betas)
  comingSoon: {
    designPartner: string
    seeShipped: string
    designPartnerNote: string
  }
  // Generic CTAs that appear all over the site
  cta: {
    learnMore: string
    seePlaybook: string
    openTheApp: string
    contactSales: string
    readDocs: string
  }
}

export const en: Dictionary = {
  header: {
    signIn: 'Sign in',
    startFree: 'Start building',
    bookDemo: 'Book a demo',
    talkToSales: 'Talk to sales',
    seeOpenRoles: 'See open roles',
    readTheRelease: 'Read the release',
  },
  announce: {
    new: 'New',
    cta: 'Read the release',
  },
  hero: {
    eyebrow: 'UGC + shoppable video',
    headline: 'The UGC platform thousands of brands ship on.',
    sub: 'Galleries, shoppable video, reviews, rights management, AI tagging — one runtime, every storefront surface.',
    primary: 'Start 14-day trial',
    secondary: 'Open the demo store',
  },
  footer: {
    newsletter: 'Newsletter',
    nextPost: 'Get the next post first.',
    monthly: 'Monthly UGC + shoppable-video benchmarks, A/B post-mortems, product updates. No spam, unsubscribe in one click.',
    placeholder: 'you@brand.com',
    subscribe: 'Subscribe',
    noSpam: 'NO SPAM · UNSUBSCRIBE IN ONE CLICK',
    product: 'Product',
    solutions: 'Solutions',
    tools: 'Free tools',
    resources: 'Resources',
    company: 'Company',
    rights: 'All rights reserved.',
  },
  switcher: {
    label: 'Language',
    soon: 'Soon',
    soonTooltip: 'Translation in progress — viewing in English for now',
    caption: 'English · Spanish · French · German live · 6 more in translation',
    playLink: 'The play →',
  },
  comingSoon: {
    designPartner: 'Become a design partner',
    seeShipped: "See what shipped this month",
    designPartnerNote: 'Design partners get sandbox access during the private beta, a Slack channel with the product team, and a 24-month price lock on the public-launch tier.',
  },
  cta: {
    learnMore: 'Learn more',
    seePlaybook: 'See the playbook',
    openTheApp: 'Open the app',
    contactSales: 'Contact sales',
    readDocs: 'Read the docs',
  },
}

export const es: Dictionary = {
  header: {
    signIn: 'Iniciar sesión',
    startFree: 'Empezar gratis',
    bookDemo: 'Reservar demo',
    talkToSales: 'Hablar con ventas',
    seeOpenRoles: 'Ver vacantes',
    readTheRelease: 'Leer la novedad',
  },
  announce: {
    new: 'Nuevo',
    cta: 'Leer la novedad',
  },
  hero: {
    eyebrow: 'UGC + vídeo shoppable',
    headline: 'La plataforma de UGC sobre la que envían miles de marcas.',
    sub: 'Galerías, vídeo shoppable, reseñas, gestión de derechos y etiquetado con IA — un solo runtime, todas las superficies de tu tienda.',
    primary: 'Prueba de 14 días',
    secondary: 'Abrir tienda demo',
  },
  footer: {
    newsletter: 'Newsletter',
    nextPost: 'Recibe el próximo artículo el primero.',
    monthly: 'Benchmarks mensuales de UGC + vídeo shoppable, post-mortems de A/B, novedades del producto. Sin spam, te desuscribes en un clic.',
    placeholder: 'tu@marca.com',
    subscribe: 'Suscribirme',
    noSpam: 'SIN SPAM · DESUSCRIBIRSE EN UN CLIC',
    product: 'Producto',
    solutions: 'Soluciones',
    tools: 'Herramientas gratuitas',
    resources: 'Recursos',
    company: 'Empresa',
    rights: 'Todos los derechos reservados.',
  },
  switcher: {
    label: 'Idioma',
    soon: 'Pronto',
    soonTooltip: 'Traducción en curso — viéndolo en inglés por ahora',
    caption: 'Inglés · español · francés · alemán activos · 6 más en traducción',
    playLink: 'La jugada →',
  },
  comingSoon: {
    designPartner: 'Convertirse en socio de diseño',
    seeShipped: 'Ver lo que enviamos este mes',
    designPartnerNote: 'Los socios de diseño obtienen acceso sandbox durante la beta privada, un canal de Slack con el equipo de producto, y un precio garantizado por 24 meses al lanzamiento público.',
  },
  cta: {
    learnMore: 'Saber más',
    seePlaybook: 'Ver la jugada',
    openTheApp: 'Abrir la app',
    contactSales: 'Contactar ventas',
    readDocs: 'Leer la documentación',
  },
}

export const fr: Dictionary = {
  header: {
    signIn: 'Se connecter',
    startFree: 'Commencer gratuitement',
    bookDemo: 'Réserver une démo',
    talkToSales: 'Parler aux ventes',
    seeOpenRoles: 'Voir les postes',
    readTheRelease: 'Lire la nouveauté',
  },
  announce: {
    new: 'Nouveau',
    cta: 'Lire la nouveauté',
  },
  hero: {
    eyebrow: 'UGC + vidéo shoppable',
    headline: 'La plateforme UGC sur laquelle des milliers de marques expédient.',
    sub: 'Galeries, vidéo shoppable, avis, gestion des droits, étiquetage IA — un seul runtime, toutes les surfaces de votre boutique.',
    primary: 'Essai 14 jours',
    secondary: 'Ouvrir la boutique démo',
  },
  footer: {
    newsletter: 'Newsletter',
    nextPost: "Recevez le prochain article en premier.",
    monthly: "Benchmarks mensuels UGC + vidéo shoppable, post-mortems d'A/B, nouveautés produit. Pas de spam, désinscription en un clic.",
    placeholder: 'vous@marque.com',
    subscribe: "S'abonner",
    noSpam: 'PAS DE SPAM · DÉSINSCRIPTION EN UN CLIC',
    product: 'Produit',
    solutions: 'Solutions',
    tools: 'Outils gratuits',
    resources: 'Ressources',
    company: 'Entreprise',
    rights: 'Tous droits réservés.',
  },
  switcher: {
    label: 'Langue',
    soon: 'Bientôt',
    soonTooltip: 'Traduction en cours — affichage en anglais pour le moment',
    caption: 'Anglais · espagnol · français · allemand en ligne · 6 autres en traduction',
    playLink: 'La méthode →',
  },
  comingSoon: {
    designPartner: 'Devenir partenaire de conception',
    seeShipped: 'Voir ce qui a été livré ce mois-ci',
    designPartnerNote: "Les partenaires de conception bénéficient d'un accès sandbox pendant la bêta privée, d'un canal Slack avec l'équipe produit, et d'un prix verrouillé 24 mois au tarif de lancement public.",
  },
  cta: {
    learnMore: 'En savoir plus',
    seePlaybook: 'Voir la méthode',
    openTheApp: "Ouvrir l'app",
    contactSales: 'Contacter les ventes',
    readDocs: 'Lire la doc',
  },
}

export const de: Dictionary = {
  header: {
    signIn: 'Anmelden',
    startFree: 'Kostenlos starten',
    bookDemo: 'Demo buchen',
    talkToSales: 'Mit Vertrieb sprechen',
    seeOpenRoles: 'Offene Stellen',
    readTheRelease: 'Release lesen',
  },
  announce: {
    new: 'Neu',
    cta: 'Release lesen',
  },
  hero: {
    eyebrow: 'UGC + Shoppable-Video',
    headline: 'Die UGC-Plattform, mit der tausende Marken arbeiten.',
    sub: 'Galerien, Shoppable-Video, Bewertungen, Rights-Management, KI-Tagging — eine Runtime, jede Storefront-Oberfläche.',
    primary: '14-Tage-Test',
    secondary: 'Demo-Store öffnen',
  },
  footer: {
    newsletter: 'Newsletter',
    nextPost: 'Den nächsten Beitrag zuerst erhalten.',
    monthly: 'Monatliche UGC- + Shoppable-Video-Benchmarks, A/B-Post-Mortems, Produkt-Updates. Kein Spam, Abmeldung mit einem Klick.',
    placeholder: 'du@marke.com',
    subscribe: 'Abonnieren',
    noSpam: 'KEIN SPAM · ABMELDUNG MIT EINEM KLICK',
    product: 'Produkt',
    solutions: 'Lösungen',
    tools: 'Kostenlose Tools',
    resources: 'Ressourcen',
    company: 'Unternehmen',
    rights: 'Alle Rechte vorbehalten.',
  },
  switcher: {
    label: 'Sprache',
    soon: 'Bald',
    soonTooltip: 'Übersetzung läuft — wird vorerst auf Englisch angezeigt',
    caption: 'Englisch · Spanisch · Französisch · Deutsch live · 6 weitere in Übersetzung',
    playLink: 'Der Plan →',
  },
  comingSoon: {
    designPartner: 'Design-Partner werden',
    seeShipped: 'Sehen, was diesen Monat geliefert wurde',
    designPartnerNote: 'Design-Partner erhalten Sandbox-Zugang während der privaten Beta, einen Slack-Kanal mit dem Produktteam und einen Preis-Lock von 24 Monaten auf das Public-Launch-Tier.',
  },
  cta: {
    learnMore: 'Mehr erfahren',
    seePlaybook: 'Den Plan ansehen',
    openTheApp: 'App öffnen',
    contactSales: 'Vertrieb kontaktieren',
    readDocs: 'Dokumentation lesen',
  },
}

export const DICTS: Record<LocaleCode, Dictionary> = { en, es, fr, de }

/** Safe lookup — falls back to English if a key is missing in the target. */
export function tFor<P extends keyof Dictionary, K extends keyof Dictionary[P]>(
  locale: LocaleCode,
  group: P,
  key: K,
): Dictionary[P][K] {
  return (DICTS[locale]?.[group]?.[key] ?? DICTS.en[group][key]) as Dictionary[P][K]
}
